import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Search, ArrowRight, Tag, SlidersHorizontal, Loader2 } from 'lucide-react';
import { useProducts } from '../hooks/useApi';
import ColorAmbient from '../components/ColorAmbient';
import PrinterStrip from '../components/PrinterStrip';
import RegistrationMark from '../components/RegistrationMark';
import BrandLogo from '../components/BrandLogo';

const Shop = () => {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');
  const [sort, setSort] = useState('default');

  const params = useMemo(() => {
    const p = {};
    if (cat !== 'All') p.category = cat;
    if (q.trim()) p.search = q.trim();
    if (sort !== 'default') p.sort = sort;
    return p;
  }, [q, cat, sort]);

  const { data: products, loading } = useProducts(params);
  // Get all categories from full list (separate fetch with no filters)
  const { data: allProducts } = useProducts({});
  const cats = ['All', ...Array.from(new Set((allProducts || []).map((p) => p.category)))];
  const filtered = products || [];

  return (
    <main className="pt-32 pb-24 bg-white min-h-screen relative overflow-hidden">
      <ColorAmbient variant="rainbow" intensity="subtle" />

      {/* Registration marks in corners */}
      <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
      <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

      {/* Header */}
      <section className="container-pn mb-12 relative">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3">Catalog</p>
            <h1 className="flex flex-wrap items-center gap-x-4 gap-y-2 text-5xl md:text-6xl font-black text-[#050F2E] tracking-tight">
              <span>Shop</span>
              <BrandLogo brand="proofnation" imgClassName="h-10 md:h-14" testId="shop-heading-logo" />
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl">
              We work with financing options, facilitating your growth. Explore our complete catalog of industry-leading proofing solutions.
            </p>
            <div className="mt-6 max-w-md">
              <PrinterStrip className="opacity-90" />
            </div>
          </div>
          <div className="text-sm text-slate-500 font-semibold">{filtered.length} products</div>
        </div>
      </section>

      {/* Filters */}
      <section className="container-pn mb-10">
        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 flex flex-col md:flex-row gap-4 items-stretch md:items-center">
          <div className="flex-1 relative">
            <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search by name, code, brand..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-11 h-12 rounded-xl bg-white border-slate-200"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm font-bold border-2 btn-transition ${
                  cat === c
                    ? 'bg-[#0A2A63] text-white border-[#0A2A63]'
                    : 'bg-white text-[#0A2A63] border-slate-200 hover:border-[#0A2A63]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-slate-500" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="h-12 px-4 rounded-xl bg-white border border-slate-200 text-sm font-semibold text-[#0A2A63] focus:outline-none focus:ring-2 focus:ring-[#0A2A63]"
            >
              <option value="default">Sort: Default</option>
              <option value="price-asc">Price: Low to high</option>
              <option value="price-desc">Price: High to low</option>
              <option value="name">Name</option>
            </select>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="container-pn">
        {loading ? (
          <div className="text-center py-20 flex items-center justify-center gap-3 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="font-semibold">Loading products...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-500">No products found.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Link
                to={`/shop/${p.id}`}
                key={p.id}
                className="group bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl border border-slate-100 hover:border-[#0A2A63] btn-transition flex flex-col"
              >
                <div className="relative h-64 bg-gradient-to-br from-slate-50 to-slate-100 p-6 flex items-center justify-center overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-[#0A2A63] text-white px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest">
                    <Tag className="h-3 w-3" /> {p.code}
                  </div>
                  <div className="absolute top-4 right-4 bg-[#FF4E50] text-[#050F2E] px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {p.brand}
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="text-lg font-bold leading-tight mb-2 text-[#050F2E]">{p.name}</h3>
                  <p className="text-sm text-slate-600 line-clamp-2 mb-5 flex-1">{p.shortDescription}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      {p.price ? (
                        <>
                          <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">From</p>
                          <p className="text-xl font-black text-[#0A2A63]">${p.price.toLocaleString()}</p>
                        </>
                      ) : (
                        <p className="text-sm font-bold text-[#0A2A63]">{p.priceLabel || 'Contact us'}</p>
                      )}
                    </div>
                    <span className="inline-flex items-center text-sm font-bold text-[#0A2A63] group-hover:text-[#FF4E50] transition-colors">
                      See product <ArrowRight className="h-4 w-4 ml-1" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default Shop;
